/**
 * Générateur — Calculer des probabilités dans des situations simples d'équiprobabilité
 * Attendu ID : 680 | Niveau : 6ème | Domaine : Organisation et gestion de données et probabilités
 *
 * Templates exercices (niveau didactique) :
 *   - calcul_probabilite_equiprobabilite  (qcm_unique, qcm_multiple, texte, relier, ordonner, tableau_vf, texte_trou_saisie, texte_trou_drag)
 *   - comparer_probabilites_tirages       (qcm_unique, ordonner, texte)
 *   - fabriquer_situation_probabilite     (texte, classer, qcm_multiple)  ← direction inverse
 *
 * Labels de référence :
 *   - Calculer une probabilité en situation d'équiprobabilité
 *   - Comparer des probabilités dans plusieurs tirages
 *   - Fabriquer une situation pour une probabilité donnée
 *
 * Variantes estimées : ~3000+
 */

import {
    randomChoice, randomChoiceMultiple, randInt, shuffle,
    makeQcm, makeSaisie, makeVraiFaux, makeTexteTrou, makeRelier, makeOrdonner, makeClasser,
    filtrerDistracteurs,
} from '../../../utils';

// ─── Constantes ───
const ATTENDU_ID = 680;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_CALCUL    = 'Calculer une probabilité en situation d\'équiprobabilité';
const L_COMPARER  = 'Comparer des probabilités dans plusieurs tirages';
const L_FABRIQUER = 'Fabriquer une situation pour une probabilité donnée';

// ─── Helpers mathématiques ───

const pgcd = (a, b) => (b === 0 ? a : pgcd(b, a % b));

const simplifier = (num, den) => {
    const g = pgcd(num, den);
    return { num: num / g, den: den / g };
};

const fractionStr = (num, den) => {
    const s = simplifier(num, den);
    return `${s.num}/${s.den}`;
};

const decimalStr = (num, den) => {
    // toFixed(6) (retouche 2026-07-28) : le gate « décimal exact » laisse
    // passer des dénominateurs comme 16 dont l'écriture exacte a 4 décimales —
    // toFixed(3) produisait « 3/16 = 0,188 », égalité fausse. 6 décimales
    // couvrent tous les dénominateurs atteignables (total ≤ 20 → 2^a·5^b ≤ 16
    // exige a ≤ 4, b ≤ 1 : au pire 6 décimales pour 1/64… hors pool ; 4 pour
    // 1/16), le trim retire les zéros.
    const val = num / den;
    return val.toFixed(6).replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '').replace('.', ',');
};

const pourcentageStr = (num, den) => {
    const pct = (num / den) * 100;
    if (pct === Math.floor(pct)) return `${pct} %`;
    return `${pct.toFixed(1).replace('.', ',')} %`;
};

// ─── Pools de données ───

// Couleurs avec les 4 formes d'accord : masc sg / masc pl / fém sg / fém pl
const COULEURS = [
    { ms: 'rouge',  mp: 'rouges',  fs: 'rouge',    fp: 'rouges' },
    { ms: 'vert',   mp: 'verts',   fs: 'verte',    fp: 'vertes' },
    { ms: 'bleu',   mp: 'bleus',   fs: 'bleue',    fp: 'bleues' },
    { ms: 'jaune',  mp: 'jaunes',  fs: 'jaune',    fp: 'jaunes' },
    { ms: 'noir',   mp: 'noirs',   fs: 'noire',    fp: 'noires' },
    { ms: 'blanc',  mp: 'blancs',  fs: 'blanche',  fp: 'blanches' },
    { ms: 'rose',   mp: 'roses',   fs: 'rose',     fp: 'roses' },
    { ms: 'orange', mp: 'orange',  fs: 'orange',   fp: 'orange' },
];

const PRENOMS = [
    { n: 'Léo', fem: false }, { n: 'Théa', fem: true }, { n: 'Maxence', fem: false },
    { n: 'Amina', fem: true }, { n: 'Lina', fem: true }, { n: 'Hugo', fem: false },
    { n: 'Mila', fem: true }, { n: 'Ethan', fem: false }, { n: 'Alice', fem: true },
    { n: 'Noa', fem: false }, { n: 'Aline', fem: true }, { n: 'Baptiste', fem: false },
    { n: 'Clara', fem: true }, { n: 'Sacha', fem: false }, { n: 'Inès', fem: true },
];

// Objets avec genre
const OBJETS = [
    { pl: 'boules',  sg: 'boule',  g: 'f' },
    { pl: 'billes',  sg: 'bille',  g: 'f' },
    { pl: 'jetons',  sg: 'jeton',  g: 'm' },
    { pl: 'perles',  sg: 'perle',  g: 'f' },
];

// Accord couleur selon l'objet et le nombre
const couleurAccord = (c, objet, pluriel = false) =>
    pluriel ? (objet.g === 'f' ? c.fp : c.mp) : (objet.g === 'f' ? c.fs : c.ms);

// Accord automatique selon le nombre (1 → singulier, sinon pluriel)
const couleurNb = (c, objet, nb) => couleurAccord(c, objet, nb > 1);

const articleSg = (objet) => objet.g === 'f' ? 'une' : 'un';

// ─── Template 1 : calcul_probabilite_equiprobabilite ───

// Génère une composition aléatoire (2 couleurs, total entre 5 et 20)
const genCompositionUrne = () => {
    const total = randInt(5, 20);
    const couleurs = randomChoiceMultiple(COULEURS, 2);
    const nb1 = randInt(1, total - 1);
    const nb2 = total - nb1;
    return {
        total,
        objet: randomChoice(OBJETS),
        composition: [
            { couleur: couleurs[0], nb: nb1 },
            { couleur: couleurs[1], nb: nb2 },
        ],
    };
};

// Pour les calculs avec 3 couleurs (total entre 6 et 15)
const genCompositionTrio = () => {
    const total = randInt(6, 15);
    const couleurs = randomChoiceMultiple(COULEURS, 3);
    const nb1 = randInt(1, total - 2);
    const nb2 = randInt(1, total - nb1 - 1);
    const nb3 = total - nb1 - nb2;
    return {
        total,
        objet: randomChoice(OBJETS),
        composition: [
            { couleur: couleurs[0], nb: nb1 },
            { couleur: couleurs[1], nb: nb2 },
            { couleur: couleurs[2], nb: nb3 },
        ],
    };
};

const descCompositionUrne = (comp) => {
    // Ex : "10 jetons : 3 rouges, 7 noirs" / "10 boules : 3 rouges, 7 noires"
    // Accord singulier quand nb = 1
    const parts = comp.composition.map(c =>
        `${c.nb} ${couleurAccord(c.couleur, comp.objet, c.nb > 1)}`
    );
    return `${comp.total} ${comp.objet.pl} : ${parts.join(', ')}`;
};

const genCalculQcm = () => {
    const estTrio = Math.random() >= 0.5;
    const comp = estTrio ? genCompositionTrio() : genCompositionUrne();
    // En trio, la couleur demandée est la MAJORITAIRE une fois sur deux
    // (revue 2026-08-04) : demandée au hasard, elle était presque toujours
    // minoritaire face aux deux autres réunies → la bonne réponse restait la
    // plus petite case (« je coche le min » : 60,7 % → ~55 %).
    const choisi = estTrio && Math.random() < 0.5
        ? comp.composition.reduce((a, b) => (b.nb > a.nb ? b : a))
        : randomChoice(comp.composition);
    const nb = choisi.nb;
    const total = comp.total;
    const couleurSg = couleurAccord(choisi.couleur, comp.objet, false);
    const objetSg = comp.objet.sg;
    const art = articleSg(comp.objet);

    // Bonne réponse : fraction non simplifiée (nb/total) -- accepte aussi simplifié
    const bonne = `${nb}/${total}`;
    const complement = total - nb;
    // Set revu 2026-08-04 (décision utilisateur) : ratio favorables/défavorables
    // (« 3/7 », l'erreur de cote), complément, et UN SEUL piège hors-bornes —
    // avant, deux pièges > 1 systématiques rendaient la bonne réponse jamais
    // max et min dans 65 % des cas (« je coche la plus petite »).
    const valeurDe = (s) => (s.includes('/') ? s.split('/').reduce((a, b) => Number(a) / Number(b)) : Number(s));
    const distUniques = [];
    const valsVues = new Set([valeurDe(bonne)]);
    const pousse = (s) => {
        const v = valeurDe(s);
        if (!valsVues.has(v)) { valsVues.add(v); distUniques.push(s); }
    };
    // 1. ratio favorables/défavorables — dans le sens qui reste < 1 (sinon il
    //    deviendrait un second hors-bornes) ; si nb = complément (ratio = 1),
    //    repli sur un proche construit avec les nombres de l'énoncé
    if (nb < complement) pousse(`${nb}/${complement}`);
    else if (complement < nb) pousse(`${complement}/${nb}`);
    else pousse(`${nb}/${total + 2}`);
    // 2. complément
    pousse(`${complement}/${total}`);
    // 3. hors-bornes, un seul : inversion ou effectif brut
    pousse(Math.random() < 0.5 ? `${total}/${nb}` : `${nb}`);
    // Secours (collisions de valeur, cas limites)
    pousse(`${nb + 1}/${total}`);
    pousse(`${nb}/${total + 1}`);

    const choix = shuffle([bonne, ...distUniques.slice(0, 3)]);
    const texte = `<p>Une urne contient ${descCompositionUrne(comp)}, indiscernables au toucher. On tire au hasard ${art} ${objetSg}, sans regarder.</p><p>Quelle est la probabilité d'obtenir ${art} ${objetSg} ${couleurSg} ?</p>`;
    const payload = makeQcm(choix, bonne, texte, opts(L_CALCUL));
    payload._meta.generatorKey = 'calcul_probabilite_equiprobabilite';
    return payload;
};

const genCalculQcmMulti = () => {
    // Probabilité cible simple
    const cibles = [
        { num: 1, den: 2 }, { num: 1, den: 4 }, { num: 1, den: 3 },
        { num: 3, den: 4 }, { num: 1, den: 5 }, { num: 2, den: 5 }, { num: 3, den: 5 },
    ];
    const cible = randomChoice(cibles);
    const frac = `${cible.num}/${cible.den}`;
    // Décimal : écriture exacte si elle est finie, sinon ARRONDI AU CENTIÈME
    // (revue 2026-08-04 : « 0,333333 » illisible et présenté comme égalité —
    // la question assume désormais « valeur exacte ou approchée »).
    let dRed = cible.den / pgcd(cible.num, cible.den);
    while (dRed % 2 === 0) dRed /= 2;
    while (dRed % 5 === 0) dRed /= 5;
    const dec = dRed === 1
        ? decimalStr(cible.num, cible.den)
        : (cible.num / cible.den).toFixed(2).replace('.', ',');
    const pct = pourcentageStr(cible.num, cible.den);
    const fracX2 = `${cible.num * 2}/${cible.den * 2}`;
    const fracX3 = `${cible.num * 3}/${cible.den * 3}`;

    // Refonte revue 2026-08-04 : la fraction de l'énoncé n'est PLUS un choix
    // (bonne gratuite), le nombre de bonnes varie (quota 4/6 figé avant), et
    // le pool de fausses est élargi (42 exercices possibles avant).
    const nbBonnes = randInt(2, 4);
    const bonnes = randomChoiceMultiple([dec, pct, fracX2, fracX3], nbBonnes);

    const val = cible.num / cible.den;
    const valeurDe = (s) => (s.includes('/')
        ? s.split('/').reduce((a, b) => Number(a) / Number(b))
        : parseFloat(s.replace('%', '').replace(',', '.')) / (s.includes('%') ? 100 : 1));
    // Fausses : erreurs d'élève réelles, à DISTANCE ≥ 0,08 de la cible (la
    // question accepte les valeurs approchées, une fausse proche deviendrait
    // défendable) et dédupliquées par valeur.
    const fausseCandidates = [
        `${cible.num + 1}/${cible.den}`,               // un cran de trop
        `${cible.num}/${cible.den + 1}`,               // dénominateur décalé
        (val + 0.1).toFixed(2).replace('.', ','),       // décimal décalé
        `${cible.den}/${cible.num}`,                    // inversion
        `${cible.num}/${cible.den * 2}`,                // la moitié
        `${cible.num * 2}/${cible.den}`,                // le double
        `${cible.den - cible.num}/${cible.den}`,        // le complément
    ];
    const valsVues = new Set(bonnes.map((b) => valeurDe(b).toFixed(6)));
    const fausses = [];
    for (const f of shuffle(fausseCandidates)) {
        const v = valeurDe(f);
        if (Math.abs(v - val) < 0.08) continue;
        const clef = v.toFixed(6);
        if (valsVues.has(clef)) continue;
        valsVues.add(clef);
        fausses.push(f);
        if (fausses.length >= 6 - nbBonnes) break;
    }
    // Filet (jamais atteint sur les cibles actuelles, vérifié) : compléter
    // avec des crans supplémentaires au numérateur.
    for (let k = 2; fausses.length < 6 - nbBonnes && k < 8; k++) {
        const f = `${cible.num + k}/${cible.den}`;
        const v = valeurDe(f);
        if (Math.abs(v - val) >= 0.08 && !valsVues.has(v.toFixed(6))) {
            valsVues.add(v.toFixed(6));
            fausses.push(f);
        }
    }
    const allChoix = shuffle([...bonnes, ...fausses]);

    const texte = `<p>La probabilité d'obtenir un certain résultat vaut <strong>${frac}</strong>. Parmi les écritures suivantes, lesquelles correspondent à cette probabilité, en valeur exacte ou approchée ?</p>`;
    const payload = makeQcm(allChoix, bonnes, texte, opts(L_CALCUL), true);
    payload._meta.generatorKey = 'calcul_probabilite_equiprobabilite';
    return payload;
};

const genCalculTexte = () => {
    // Dé équilibré (1 tirage sur 3) ou urne (2 sur 3) — revue 2026-08-04 :
    // à 50/50 avec 15 énoncés dé seulement, le mode pauvre créait 8,6
    // doublons par lot de 40. Dé enrichi : 5 tailles de dés, multiples de
    // 4 et 5, et seuil k VARIABLE pour « supérieur ou égal à k ».
    const dice = Math.random() < 1 / 3;
    if (dice) {
        const faces = randomChoice([6, 8, 10, 12, 20]);
        const k = randInt(2, faces - 2);
        const evenements = [
            { desc: 'un multiple de 3', nb: Math.floor(faces / 3) },
            { desc: 'un multiple de 4', nb: Math.floor(faces / 4) },
            { desc: 'un multiple de 5', nb: Math.floor(faces / 5) },
            { desc: 'un nombre pair', nb: Math.floor(faces / 2) },
            { desc: 'un nombre impair', nb: Math.ceil(faces / 2) },
            { desc: `un nombre supérieur ou égal à ${k}`, nb: faces - k + 1 },
            { desc: 'un 6', nb: 1 },
        ];
        const evt = randomChoice(evenements);
        const nb = evt.nb;
        const bonne = fractionStr(nb, faces);
        const texte = `<p>On lance un dé équilibré à ${faces} faces, numérotées de 1 à ${faces}. Calcule la probabilité d'obtenir ${evt.desc}. Donne ta réponse sous forme de fraction simplifiée.</p>`;
        const payload = makeSaisie(bonne, texte, opts(L_CALCUL));
        payload._meta.generatorKey = 'calcul_probabilite_equiprobabilite';
        return payload;
    } else {
        const comp = genCompositionUrne();
        const choisi = randomChoice(comp.composition);
        const bonne = fractionStr(choisi.nb, comp.total);
        const couleurSg = couleurAccord(choisi.couleur, comp.objet, false);
        const objetSg = comp.objet.sg;
        const art = articleSg(comp.objet);
        const texte = `<p>Une urne contient ${descCompositionUrne(comp)}. On tire au hasard ${art} ${objetSg}. Quelle est la probabilité d'obtenir ${art} ${objetSg} ${couleurSg} ? Donne ta réponse sous forme de fraction simplifiée.</p>`;
        const payload = makeSaisie(bonne, texte, opts(L_CALCUL));
        payload._meta.generatorKey = 'calcul_probabilite_equiprobabilite';
        return payload;
    }
};

const genCalculRelier = () => {
    // Pool élargi (revue 2026-08-04, avant : 9 situations / 7 probas → 85
    // exercices possibles, 8,4 doublons par lot) : 19 situations, 12 probas.
    // Les cibles restent en écriture SIMPLIFIÉE, même quand la situation donne
    // une fraction à simplifier (2/6 → 1/3) : un pas pédagogique de plus.
    const situations = [
        { desc: 'Obtenir Pile avec une pièce équilibrée', proba: '1/2' },
        { desc: 'Obtenir un nombre pair avec un dé à 6 faces', proba: '1/2' },
        { desc: 'Obtenir un 6 avec un dé à 6 faces', proba: '1/6' },
        { desc: 'Obtenir un 2 avec un dé à 6 faces', proba: '1/6' },
        { desc: 'Tirer une boule rouge dans une urne de 1 rouge et 3 bleues', proba: '1/4' },
        { desc: 'Obtenir un secteur rouge sur une roue de 8 secteurs dont 2 rouges', proba: '1/4' },
        { desc: 'Obtenir un multiple de 3 avec un dé à 6 faces', proba: '1/3' },
        { desc: 'Tirer une boule rouge dans une urne de 2 rouges et 4 bleues', proba: '1/3' },
        { desc: 'Tirer une boule verte dans une urne de 3 vertes et 7 blanches', proba: '3/10' },
        { desc: 'Tirer un jeton noir dans un sac de 3 noirs et 7 jaunes', proba: '3/10' },
        { desc: 'Tirer le roi de cœur dans un jeu de 32 cartes', proba: '1/32' },
        { desc: 'Tirer la dame de pique dans un jeu de 32 cartes', proba: '1/32' },
        { desc: 'Tirer une boule blanche dans une urne de 3 boules blanches et 2 noires', proba: '3/5' },
        { desc: 'Obtenir un secteur vert sur une roue de 10 secteurs dont 6 verts', proba: '3/5' },
        { desc: 'Tirer une bille jaune dans un sac de 2 jaunes et 8 noires', proba: '1/5' },
        { desc: 'Obtenir un 7 avec un dé à 10 faces', proba: '1/10' },
        { desc: 'Tirer une perle rose dans un sac de 4 roses et 6 blanches', proba: '2/5' },
        { desc: 'Obtenir un secteur bleu sur une roue de 4 secteurs dont 3 bleus', proba: '3/4' },
        { desc: 'Obtenir un secteur jaune sur une roue de 8 secteurs dont 1 jaune', proba: '1/8' },
    ];
    // Tirer 4 situations à probabilités distinctes — comparées PAR VALEUR
    // (revue 2026-08-04) : « 2/6 » et « 1/3 » ne doivent jamais cohabiter.
    const valeurProba = (s) => { const [a, b] = s.split('/').map(Number); return (a / b).toFixed(6); };
    const shuffled = shuffle(situations);
    const chosen = [];
    const probasUtilisees = new Set();
    for (const s of shuffled) {
        if (!probasUtilisees.has(valeurProba(s.proba))) {
            chosen.push(s);
            probasUtilisees.add(valeurProba(s.proba));
            if (chosen.length === 4) break;
        }
    }
    const pairs = chosen.map(c => ({ source: c.desc, target: c.proba }));
    const payload = makeRelier(pairs, '<p>Relie chaque situation à la probabilité correspondante.</p>', opts(L_CALCUL));
    payload._meta.generatorKey = 'calcul_probabilite_equiprobabilite';
    return payload;
};

const genCalculOrdonner = () => {
    // Large pool de situations avec probabilités distinctes
    const situations = [
        { desc: 'Obtenir Pile avec une pièce équilibrée', val: 1 / 2 },
        { desc: 'Obtenir un 3 avec un dé à 6 faces', val: 1 / 6 },
        { desc: 'Obtenir un nombre pair avec un dé à 6 faces', val: 1 / 2 },
        { desc: 'Tirer une boule rouge dans un sac de 3 rouges et 17 vertes', val: 3 / 20 },
        { desc: 'Obtenir un secteur bleu sur une roue de 4 secteurs dont 3 bleus', val: 3 / 4 },
        { desc: 'Obtenir un multiple de 4 avec un dé à 12 faces', val: 3 / 12 },
        { desc: 'Tirer une boule noire dans un sac de 4 noires et 6 blanches', val: 4 / 10 },
        { desc: 'Obtenir un 1 avec un dé à 10 faces', val: 1 / 10 },
        { desc: 'Obtenir un multiple de 3 avec un dé à 6 faces', val: 1 / 3 },
        { desc: 'Tirer une carte cœur dans un jeu de 32 cartes', val: 8 / 32 },
        { desc: 'Tirer un jeton jaune dans un sac de 2 jaunes et 8 verts', val: 2 / 10 },
        { desc: 'Obtenir un secteur rouge sur une roue de 5 secteurs dont 4 rouges', val: 4 / 5 },
        { desc: 'Obtenir un nombre supérieur à 4 avec un dé à 6 faces', val: 2 / 6 },
        { desc: 'Tirer une perle rose dans un sac de 7 roses et 3 oranges', val: 7 / 10 },
        { desc: 'Obtenir un nombre premier avec un dé à 10 faces', val: 4 / 10 },
        { desc: 'Tirer le roi de cœur dans un jeu de 32 cartes', val: 1 / 32 },
        { desc: 'Obtenir un secteur bleu sur une roue de 16 secteurs dont 5 bleus', val: 5 / 16 },
        // Enrichissement revue 2026-08-04 : 12 situations pour nourrir les
        // chaînes de dénominateurs multiples (10/16/20/32).
        { desc: 'Tirer une boule bleue dans un sac de 3 bleues et 7 rouges', val: 3 / 10 },
        { desc: 'Tirer un jeton vert dans un sac de 6 verts et 4 noirs', val: 6 / 10 },
        { desc: 'Tirer une perle blanche dans un sac de 8 blanches et 2 roses', val: 8 / 10 },
        { desc: 'Obtenir un nombre inférieur ou égal à 9 avec un dé à 10 faces', val: 9 / 10 },
        { desc: 'Tirer un billet gagnant parmi 20 billets dont 17 gagnants', val: 17 / 20 },
        { desc: 'Tirer une boule jaune dans un sac de 1 jaune et 19 vertes', val: 1 / 20 },
        { desc: 'Obtenir un secteur rouge sur une roue de 16 secteurs dont 11 rouges', val: 11 / 16 },
        { desc: 'Obtenir un secteur noir sur une roue de 16 secteurs dont 15 noirs', val: 15 / 16 },
        { desc: 'Tirer une carte rouge dans un jeu de 32 cartes', val: 16 / 32 },
        { desc: 'Tirer une carte pique dans un jeu de 32 cartes', val: 8 / 32 },
        { desc: 'Obtenir un secteur bleu sur une roue de 4 secteurs dont 1 bleu', val: 1 / 4 },
        // Définition rappelée (revue 2026-08-04) : « figure » est du vocabulaire
        // de jeu, pas de maths — sans elle, l'élève ne peut pas compter les 12.
        { desc: 'Tirer une figure (valet, dame ou roi) dans un jeu de 32 cartes', val: 12 / 32 },
    ];
    // Revue 2026-08-04 (décision utilisateur) :
    //   · écart minimal de 0,1 entre les 4 valeurs — départager 7/10 et 3/4
    //     exige la réduction au même dénominateur, technique de 5ème ;
    //   · 70 % des tirages viennent d'une CHAÎNE de dénominateurs multiples
    //     (comparaison strictement au programme de 6ème), 30 % du modèle libre.
    // Dénominateur « naturel » (celui que l'élève écrit) par situation :
    const DEN = {
        'Obtenir Pile avec une pièce équilibrée': 2,
        'Obtenir un 3 avec un dé à 6 faces': 6,
        'Obtenir un nombre pair avec un dé à 6 faces': 6,
        'Tirer une boule rouge dans un sac de 3 rouges et 17 vertes': 20,
        'Obtenir un secteur bleu sur une roue de 4 secteurs dont 3 bleus': 4,
        'Obtenir un multiple de 4 avec un dé à 12 faces': 12,
        'Tirer une boule noire dans un sac de 4 noires et 6 blanches': 10,
        'Obtenir un 1 avec un dé à 10 faces': 10,
        'Obtenir un multiple de 3 avec un dé à 6 faces': 6,
        'Tirer une carte cœur dans un jeu de 32 cartes': 32,
        'Tirer un jeton jaune dans un sac de 2 jaunes et 8 verts': 10,
        'Obtenir un secteur rouge sur une roue de 5 secteurs dont 4 rouges': 5,
        'Obtenir un nombre supérieur à 4 avec un dé à 6 faces': 6,
        'Tirer une perle rose dans un sac de 7 roses et 3 oranges': 10,
        'Obtenir un nombre premier avec un dé à 10 faces': 10,
        'Tirer le roi de cœur dans un jeu de 32 cartes': 32,
        'Obtenir un secteur bleu sur une roue de 16 secteurs dont 5 bleus': 16,
        'Tirer une boule bleue dans un sac de 3 bleues et 7 rouges': 10,
        'Tirer un jeton vert dans un sac de 6 verts et 4 noirs': 10,
        'Tirer une perle blanche dans un sac de 8 blanches et 2 roses': 10,
        'Obtenir un nombre inférieur ou égal à 9 avec un dé à 10 faces': 10,
        'Tirer un billet gagnant parmi 20 billets dont 17 gagnants': 20,
        'Tirer une boule jaune dans un sac de 1 jaune et 19 vertes': 20,
        'Obtenir un secteur rouge sur une roue de 16 secteurs dont 11 rouges': 16,
        'Obtenir un secteur noir sur une roue de 16 secteurs dont 15 noirs': 16,
        'Tirer une carte rouge dans un jeu de 32 cartes': 32,
        'Tirer une carte pique dans un jeu de 32 cartes': 32,
        'Obtenir un secteur bleu sur une roue de 4 secteurs dont 1 bleu': 4,
        'Tirer une figure (valet, dame ou roi) dans un jeu de 32 cartes': 32,
    };
    // La chaîne 2-6-12 est EXCLUE : ses valeurs (1/6, 1/4, 1/3) sont trop
    // serrées pour l'écart de 0,1.
    const CHAINES = [[2, 4, 16, 32], [2, 10, 20], [5, 10, 20]];

    const ecartOk = (arr) => {
        const v = arr.map((s) => s.val).sort((a, b) => a - b);
        for (let k = 1; k < v.length; k++) if (v[k] - v[k - 1] < 0.0999) return false;
        return true;
    };
    const tire4 = (pool) => {
        for (let essai = 0; essai < 80; essai++) {
            const shuffled = shuffle(pool);
            const chosen = [];
            const valsUtilisees = new Set();
            for (const s of shuffled) {
                if (!valsUtilisees.has(s.val.toFixed(3))) {
                    chosen.push(s);
                    valsUtilisees.add(s.val.toFixed(3));
                    if (chosen.length === 4) break;
                }
            }
            if (chosen.length === 4 && ecartOk(chosen)) return chosen;
        }
        return null;
    };

    let chosen = null;
    if (Math.random() < 0.7) {
        const chaine = randomChoice(CHAINES);
        chosen = tire4(situations.filter((s) => chaine.includes(DEN[s.desc])));
    }
    if (!chosen) chosen = tire4(situations);
    // Filet ultime (jamais atteint : le pool libre satisfait toujours l'écart)
    if (!chosen) chosen = situations.slice(0, 4);
    // Trier croissant
    chosen.sort((a, b) => a.val - b.val);
    const items = chosen.map(c => c.desc);
    const payload = makeOrdonner(items, '<p>Range les probabilités suivantes du <strong>plus petit</strong> au <strong>plus grand</strong>.</p>', opts(L_CALCUL));
    payload._meta.generatorKey = 'calcul_probabilite_equiprobabilite';
    return payload;
};

const genCalculTableauVf = () => {
    // Composition DÉSÉQUILIBRÉE exigée (revue 2026-08-04) : à égalité (5-5),
    // « plus de chances » était marquée Vrai (faux) et « autant de chances »
    // Faux (vrai) — 2 lignes sur 5 inversées dans 5 % des tirages.
    let comp = genCompositionUrne();
    let garde = 0;
    while (comp.composition[0].nb === comp.composition[1].nb && garde < 50) {
        comp = genCompositionUrne();
        garde++;
    }
    const majoritaire = comp.composition[0].nb >= comp.composition[1].nb ? comp.composition[0] : comp.composition[1];
    const minoritaire = comp.composition[0].nb < comp.composition[1].nb ? comp.composition[0] : comp.composition[1];
    const total = comp.total;
    const objetSg = comp.objet.sg;
    const art = articleSg(comp.objet);
    const couleurMaj = couleurAccord(majoritaire.couleur, comp.objet, false);
    const couleurMin = couleurAccord(minoritaire.couleur, comp.objet, false);

    // Élision : "que une" → "qu'une"
    const queDe = art === 'une' ? "qu'une" : "qu'un";
    // Viviers élargis + quota VARIABLE (revue 2026-08-04, avant : les 5 mêmes
    // phrases, toujours 2 Vrai / 3 Faux — l'élève déduisait les dernières
    // lignes en comptant).
    const viverVraies = [
        `La probabilité d'obtenir ${art} ${objetSg} ${couleurMaj} est ${majoritaire.nb}/${total}`,
        `Il y a plus de chances d'obtenir ${art} ${objetSg} ${couleurMaj} ${queDe} ${objetSg} ${couleurMin}`,
        `La probabilité d'obtenir ${art} ${objetSg} ${couleurMin} est ${minoritaire.nb}/${total}`,
    ];
    const viverFausses = [
        `La probabilité d'obtenir ${art} ${objetSg} ${couleurMaj} est ${majoritaire.nb}`,
        `Il y a autant de chances d'obtenir ${art} ${objetSg} ${couleurMaj} ${queDe} ${objetSg} ${couleurMin}`,
        `La probabilité d'obtenir ${art} ${objetSg} ${couleurMin} est ${total}/${minoritaire.nb}`,
        `La probabilité d'obtenir ${art} ${objetSg} ${couleurMaj} est ${minoritaire.nb}/${total}`,
    ];
    const nbVraies = randInt(2, 3);
    const vraies = randomChoiceMultiple(viverVraies, nbVraies);
    const fausses = randomChoiceMultiple(viverFausses, 5 - nbVraies);

    const lignes = [];
    const reponses = [];
    vraies.forEach(v => { lignes.push(v); reponses.push(0); });
    fausses.forEach(f => { lignes.push(f); reponses.push(1); });
    const indices = shuffle(lignes.map((_, i) => i));
    const ligneMel = indices.map(i => lignes[i]);
    const reponsesMel = indices.map(i => reponses[i]);

    const texte = `<p>Dans un sac, il y a ${descCompositionUrne(comp)}. On tire ${art} ${objetSg} au hasard. Indique si chaque affirmation est vraie ou fausse.</p>{{element:0}}`;
    const payload = makeVraiFaux(ligneMel, reponsesMel, texte, opts(L_CALCUL));
    payload._meta.generatorKey = 'calcul_probabilite_equiprobabilite';
    return payload;
};

const genCalculTexteTrouSaisie = () => {
    const comp = genCompositionUrne();
    const choisi = randomChoice(comp.composition);
    const couleurSg = couleurAccord(choisi.couleur, comp.objet, false);
    const couleurPl = couleurAccord(choisi.couleur, comp.objet, true);
    const objetSg = comp.objet.sg;
    const art = articleSg(comp.objet);

    // N'émettre le trou décimal QUE si la division tombe juste (décimal fini) : sinon
    // « nb/total = dec » pose une égalité FAUSSE (2/6 = 0,333 ≠). Décimal fini ⇔ le
    // dénominateur réduit n'a que des facteurs 2 et 5.
    // Durci revue 2026-08-04 : le dénominateur réduit doit DIVISER 100 — 3/8
    // (0,375) ou 5/16 (0,3125) sont finis mais inatteignables en 6ème, où la
    // technique est l'amplification vers une fraction décimale (/10 ou /100).
    const g = (a, b) => { a = Math.abs(a); b = Math.abs(b); while (b) { [a, b] = [b, a % b]; } return a || 1; };
    const d = comp.total / g(choisi.nb, comp.total);
    const finDec = (100 % d === 0) ? ` = {{${decimalStr(choisi.nb, comp.total)}}}` : '';

    // La composition est injectée dans la CONSIGNE (comme le format Drag) : sinon, en
    // mode saisie, total et favorables sont masqués et rien ne les détermine → réponse
    // non unique. Ici l'élève lit la composition puis complète le calcul.
    const phrase = `Dans une urne, il y a {{${comp.total}}} ${comp.objet.pl} au total dont {{${choisi.nb}}} ${comp.objet.pl} ${couleurPl}. La probabilité d'obtenir ${art} ${objetSg} ${couleurSg} est donc {{${choisi.nb}}}/{{${comp.total}}}${finDec}.`;
    const payload = makeTexteTrou(
        phrase,
        `<p>Une urne contient ${descCompositionUrne(comp)}. On tire ${art} ${objetSg} au hasard. Complète le calcul de probabilité en saisissant les bons nombres.</p>`,
        { ...opts(L_CALCUL), mode: 'saisie' },
    );
    payload._meta.generatorKey = 'calcul_probabilite_equiprobabilite';
    return payload;
};

const genCalculTexteTrouDrag = () => {
    const comp = genCompositionUrne();
    const choisi = randomChoice(comp.composition);
    const autre = comp.composition.find(c => c !== choisi);
    const couleurSg = couleurAccord(choisi.couleur, comp.objet, false);
    const couleurPl = couleurAccord(choisi.couleur, comp.objet, true);
    const objetSg = comp.objet.sg;
    const art = articleSg(comp.objet);

    const phrase = `Dans cette urne, il y a en tout {{${comp.total}}} ${comp.objet.pl}. Le nombre de ${comp.objet.pl} ${couleurPl} est {{${choisi.nb}}}. La probabilité d'obtenir ${art} ${objetSg} ${couleurSg} est donc {{${choisi.nb}/${comp.total}}}.`;
    // Filtre contre les mots attendus : deux couleurs peuvent avoir le même
    // effectif (autre.nb = choisi.nb).
    // « nb+1 » remplacé par le ratio favorables/défavorables (revue 2026-08-04) :
    // en drag, un nombre absent de l'énoncé ne tente personne — le ratio
    // matérialise l'erreur de cote, cohérente avec le set du QCM.
    const distracteurs = filtrerDistracteurs(phrase, [
        String(autre.nb),
        `${choisi.nb}/${autre.nb}`,
        `${comp.total}/${choisi.nb}`,
        `${autre.nb}/${comp.total}`,
    ]);

    const payload = makeTexteTrou(
        phrase,
        `<p>Une urne contient ${descCompositionUrne(comp)}. On tire ${art} ${objetSg} au hasard. Complète le raisonnement avec les bons éléments.</p>`,
        { ...opts(L_CALCUL), mode: 'drag', distracteurs },
    );
    payload._meta.generatorKey = 'calcul_probabilite_equiprobabilite';
    return payload;
};

// ─── Template 2 : comparer_probabilites_tirages ───

const genComparerQcm = () => {
    // 3 sacs avec mêmes objets (billes = féminin) pour simplifier l'accord
    const objet = { pl: 'billes', sg: 'bille', g: 'f' };
    const couleurCible = randomChoice(COULEURS);
    const couleurAutre = randomChoice(COULEURS.filter(c => c !== couleurCible));
    const prenoms = randomChoiceMultiple(PRENOMS, 3);
    // Revue 2026-08-04 : avant, deux sacs ex æquo en tête (3,2 % des tirages)
    // faisaient servir « Les trois ont la même probabilité » comme bonne
    // réponse — fausse, et la question n'avait aucune réponse juste.
    // Désormais : 1 tirage sur 10 construit VOLONTAIREMENT trois sacs de même
    // probabilité (fractions équivalentes, totaux différents — l'option
    // « les trois » reste vivante) ; sinon on RE-TIRE jusqu'à un max unique.
    let sacs;
    let egalite = Math.random() < 0.1;
    if (egalite) {
        const ratios = [[1, 2], [1, 3], [2, 5], [1, 5], [3, 5], [1, 4], [3, 4]];
        const [rn, rd] = randomChoice(ratios);
        const mults = shuffle([1, 2, 3, 4, 5].filter(m => m * rd >= 4 && m * rd <= 15)).slice(0, 3);
        sacs = prenoms.map((p, i) => {
            const m = mults[i] || (i + 1);
            return { prenom: p.n, fem: p.fem, total: m * rd, nbCible: m * rn };
        });
    } else {
        let garde = 0;
        do {
            sacs = prenoms.map(p => {
                const total = randInt(5, 15);
                const nbCible = randInt(1, total - 1);
                return { prenom: p.n, fem: p.fem, total, nbCible };
            });
            const vals = sacs.map(s => s.nbCible / s.total);
            const max = Math.max(...vals);
            // Max unique ET devançant chaque autre sac d'au moins 0,1 (revue
            // 2026-08-04) : un duel serré (0,625 vs 0,6) exige la technique de
            // 5ème — avec l'écart, l'élève tranche par repères.
            if (vals.filter(v => v === max).length === 1
                && vals.every(v => v === max || max - v >= 0.0999)) break;
            garde++;
        } while (garde < 50);
    }
    const valeurs = sacs.map(s => s.nbCible / s.total);
    let maxIdx = 0;
    for (let i = 1; i < sacs.length; i++) {
        if (valeurs[i] > valeurs[maxIdx]) maxIdx = i;
    }
    const gagnant = sacs[maxIdx].prenom;
    const choix = [...sacs.map(s => s.prenom), 'Les trois ont la même probabilité.'];

    const couleurCibleSg = couleurAccord(couleurCible, objet, false);

    const descSac = (s) =>
        `${s.nbCible} ${couleurNb(couleurCible, objet, s.nbCible)} et ${s.total - s.nbCible} ${couleurNb(couleurAutre, objet, s.total - s.nbCible)}`;
    const listeHtml = sacs.map(s => `<li><strong>Sac de ${s.prenom}</strong> : ${descSac(s)}.</li>`).join('');
    const allFem = sacs.every(s => s.fem);
    const texte = `<p>${sacs.map(s => s.prenom).join(', ')} ont chacun${allFem ? 'e' : ''} un sac de billes. ${allFem ? 'Chacune' : 'Chacun'} tire une bille au hasard.</p><ul>${listeHtml}</ul><p>Qui a la plus grande probabilité de tirer une bille ${couleurCibleSg} ?</p>`;
    const payload = makeQcm(choix, egalite ? 'Les trois ont la même probabilité.' : gagnant, texte, opts(L_COMPARER));
    payload._meta.generatorKey = 'comparer_probabilites_tirages';
    return payload;
};

const genComparerOrdonner = () => {
    // 4 sacs de billes (fém) avec probas distinctes
    const objet = { pl: 'billes', sg: 'bille', g: 'f' };
    const lettres = ['A', 'B', 'C', 'D'];
    const couleurCible = randomChoice(COULEURS);
    const couleurAutre = randomChoice(COULEURS.filter(c => c !== couleurCible));
    const couleurCibleSg = couleurAccord(couleurCible, objet, false);
    // Valeurs espacées de 0,1 deux à deux (revue 2026-08-04) : 76,5 % des
    // tirages contenaient une paire serrée (0,6 vs 0,625) — comparaison de
    // 5ème. Avec l'écart, le classement se fait par repères de 6ème.
    let sacs = [];
    let essais = 0;
    while (sacs.length < 4) {
        if (++essais > 200) { sacs = []; essais = 0; } // impasse gloutonne : on repart
        const total = randInt(4, 12);
        const nb = randInt(1, total - 1);
        const val = nb / total;
        if (sacs.every(s => Math.abs(s.val - val) >= 0.0999)) {
            sacs.push({ nb, total, nbAutre: total - nb, val });
        }
    }
    sacs.sort((a, b) => a.val - b.val);
    const items = sacs.map((s, i) =>
        `Sac ${lettres[i]} : ${s.nb} ${couleurNb(couleurCible, objet, s.nb)} et ${s.nbAutre} ${couleurNb(couleurAutre, objet, s.nbAutre)}`
    );
    const payload = makeOrdonner(
        items,
        `<p>Range les situations suivantes de la <strong>plus petite</strong> à la <strong>plus grande probabilité</strong> de tirer une bille ${couleurCibleSg}.</p>`,
        opts(L_COMPARER),
    );
    payload._meta.generatorKey = 'comparer_probabilites_tirages';
    return payload;
};

const genComparerTexte = () => {
    const [p1, p2] = randomChoiceMultiple(PRENOMS, 2);
    const couleurCible = randomChoice(COULEURS);
    const couleurAutre = randomChoice(COULEURS.filter(c => c !== couleurCible));
    const objet = randomChoice(OBJETS);
    const art = articleSg(objet);
    const couleurCibleSg = couleurAccord(couleurCible, objet, false);

    // Duel à écart ≥ 0,1 (revue 2026-08-04, avant : seule l'égalité stricte
    // était exclue — 17,8 % de duels serrés, indécidables en 6ème).
    let total1, nb1, total2, nb2;
    let garde = 0;
    do {
        total1 = randInt(5, 12);
        nb1 = randInt(1, total1 - 1);
        total2 = randInt(4, 10);
        nb2 = randInt(1, total2 - 1);
        garde++;
    } while (Math.abs(nb1 / total1 - nb2 / total2) < 0.0999 && garde < 100);
    const gagnant = (nb1 / total1 > nb2 / total2) ? p1 : p2;
    const chacun = (p1.fem && p2.fem) ? 'Chacune' : 'Chacun';

    const cc1 = couleurNb(couleurCible, objet, nb1);
    const ca1 = couleurNb(couleurAutre, objet, total1 - nb1);
    const cc2 = couleurNb(couleurCible, objet, nb2);
    const ca2 = couleurNb(couleurAutre, objet, total2 - nb2);
    const texte = `<p>${p1.n} a un sac avec ${nb1} ${objet.pl} ${cc1} et ${total1 - nb1} ${objet.pl} ${ca1}. ${p2.n} a un sac avec ${nb2} ${objet.pl} ${cc2} et ${total2 - nb2} ${objet.pl} ${ca2}. ${chacun} tire ${art} ${objet.sg} au hasard.</p><p>Qui a la plus grande probabilité de tirer ${art} ${objet.sg} <strong>${couleurCibleSg}</strong> ? Réponds par le prénom.</p>`;
    const payload = makeSaisie(gagnant.n, texte, opts(L_COMPARER));
    payload._meta.generatorKey = 'comparer_probabilites_tirages';
    return payload;
};

// ─── Template 3 : fabriquer_situation_probabilite (direction inverse) ───

const genFabriquerTexte = () => {
    // On fixe total N, cible p = k/N, demander k
    const cibles = [
        { num: 1, den: 2 }, { num: 1, den: 4 }, { num: 1, den: 5 },
        { num: 1, den: 10 }, { num: 3, den: 10 }, { num: 1, den: 3 },
        { num: 2, den: 5 }, { num: 3, den: 4 },
    ];
    const cible = randomChoice(cibles);
    // Choisir N multiple de den
    const multiples = [1, 2, 3, 4, 5];
    const mult = randomChoice(multiples.filter(m => m * cible.den >= 6 && m * cible.den <= 40));
    const N = (mult || 2) * cible.den;
    const k = mult * cible.num;

    const couleur = randomChoice(COULEURS);
    const couleurAutre = randomChoice(COULEURS.filter(c => c !== couleur));
    const objet = randomChoice(OBJETS);
    const art = articleSg(objet);
    // Dans l'énoncé, N est grand donc on utilise le pluriel
    const couleurPl = couleurAccord(couleur, objet, true);
    const couleurAutrePl = couleurAccord(couleurAutre, objet, true);
    const couleurSg = couleurAccord(couleur, objet, false);

    const texte = `<p>Un sac contiendra <strong>${N} ${objet.pl}</strong> au total, certain${objet.g === 'f' ? 'e' : ''}s ${couleurPl} et les autres ${couleurAutrePl}. On veut que la probabilité de tirer ${art} ${objet.sg} ${couleurSg} soit égale à <strong>${cible.num}/${cible.den}</strong>.</p><p>Combien de ${objet.pl} ${couleurPl} doit-il y avoir dans le sac ?</p>`;
    const payload = makeSaisie(String(k), texte, opts(L_FABRIQUER));
    payload._meta.generatorKey = 'fabriquer_situation_probabilite';
    return payload;
};

const genFabriquerClasser = () => {
    // Cible fixe (0,5 ou 1/4), générer 6 sacs dont 3 correspondent
    const cibles = [
        { str: '0,5', num: 1, den: 2 },
        { str: '1/4', num: 1, den: 4 },
        { str: '0,25', num: 1, den: 4 },
        { str: '1/2', num: 1, den: 2 },
    ];
    const cible = randomChoice(cibles);
    const objet = { pl: 'billes', sg: 'bille', g: 'f' };
    const couleurCible = randomChoice(COULEURS);
    const couleurAutre = randomChoice(COULEURS.filter(c => c !== couleurCible));
    const couleurCibleSg = couleurAccord(couleurCible, objet, false);

    // Quota VARIABLE (revue 2026-08-05, avant : toujours 3 sacs de chaque
    // côté — l'élève triait les derniers sans les vérifier).
    const nbConformes = randInt(2, 4);
    const matches = [];
    const nonMatches = [];
    const multsMatch = shuffle([2, 3, 4, 5]).slice(0, nbConformes);
    multsMatch.forEach(m => {
        const tot = m * cible.den;
        const nb = m * cible.num;
        matches.push(`Sac avec ${nb} ${couleurNb(couleurCible, objet, nb)} et ${tot - nb} ${couleurNb(couleurAutre, objet, tot - nb)}`);
    });
    const valCible = cible.num / cible.den;
    while (nonMatches.length < 6 - nbConformes) {
        const tot = randInt(4, 20);
        const nb = randInt(1, tot - 1);
        const sac = `Sac avec ${nb} ${couleurNb(couleurCible, objet, nb)} et ${tot - nb} ${couleurNb(couleurAutre, objet, tot - nb)}`;
        // Dédup : le tirage peut reproduire deux fois le même sac
        if (Math.abs(nb / tot - valCible) > 0.01 && !nonMatches.includes(sac)) {
            nonMatches.push(sac);
        }
    }

    const categories = [
        { name: `Probabilité = ${cible.str}`, items: matches },
        { name: `Probabilité ≠ ${cible.str}`, items: nonMatches },
    ];
    const payload = makeClasser(
        categories,
        `<p>Parmi les sacs ci-dessous, classe ceux pour lesquels la probabilité de tirer une bille ${couleurCibleSg} vaut <strong>${cible.str}</strong> et ceux pour lesquels elle ne vaut pas ${cible.str}.</p>`,
        opts(L_FABRIQUER),
    );
    payload._meta.generatorKey = 'fabriquer_situation_probabilite';
    return payload;
};

const genFabriquerQcmMulti = () => {
    const cibles = [
        { str: '1/2', val: 0.5, num: 1, den: 2 }, { str: '1/3', val: 1 / 3, num: 1, den: 3 },
        { str: '1/4', val: 0.25, num: 1, den: 4 }, { str: '1/5', val: 0.2, num: 1, den: 5 },
    ];
    const cible = randomChoice(cibles);
    // 5 situations : nbBonnes VARIABLE (revue 2026-08-05, avant : toujours 3
    // bonnes sur 5 — cochable mécaniquement une fois le quota connu).
    const nbBonnes = randInt(2, 3);
    const descrSet = new Set();
    const situations = [];
    // matchs : utiliser multiples distincts pour garantir desc distincts
    const multsMatch = shuffle([1, 2, 3, 4, 5]).slice(0, nbBonnes);
    multsMatch.forEach(m => {
        const tot = m * cible.den;
        const nb = m * cible.num;
        const desc = `Tirer une boule rouge dans un sac de ${nb} rouges et ${tot - nb} vertes`;
        if (!descrSet.has(desc)) {
            descrSet.add(desc);
            situations.push({ desc, match: true });
        }
    });
    // non-matchs : vraiment distincts du pool cible
    let tries = 0;
    while (situations.filter(s => !s.match).length < 5 - nbBonnes && tries < 50) {
        tries++;
        const tot = randInt(4, 12);
        const nb = randInt(1, tot - 1);
        if (Math.abs(nb / tot - cible.val) < 0.02) continue;
        const desc = `Tirer une boule rouge dans un sac de ${nb} rouges et ${tot - nb} vertes`;
        if (!descrSet.has(desc)) {
            descrSet.add(desc);
            situations.push({ desc, match: false });
        }
    }
    const shuffled = shuffle(situations);
    const choix = shuffled.map(s => s.desc);
    const bonnes = shuffled.filter(s => s.match).map(s => s.desc);
    const texte = `<p>Parmi les situations suivantes, lesquelles ont une probabilité égale à <strong>${cible.str}</strong> ?</p>`;
    const payload = makeQcm(choix, bonnes, texte, opts(L_FABRIQUER), true);
    payload._meta.generatorKey = 'fabriquer_situation_probabilite';
    return payload;
};

// ─── Registre ───
const GENERATORS = {
    calcul_probabilite_equiprobabilite_qcm_unique:     genCalculQcm,
    calcul_probabilite_equiprobabilite_qcm_multiple:   genCalculQcmMulti,
    calcul_probabilite_equiprobabilite_texte:          genCalculTexte,
    calcul_probabilite_equiprobabilite_relier:         genCalculRelier,
    calcul_probabilite_equiprobabilite_ordonner:       genCalculOrdonner,
    calcul_probabilite_equiprobabilite_tableau_vf:     genCalculTableauVf,
    calcul_probabilite_equiprobabilite_texte_trou_saisie: genCalculTexteTrouSaisie,
    calcul_probabilite_equiprobabilite_texte_trou_drag:   genCalculTexteTrouDrag,

    comparer_probabilites_tirages_qcm_unique: genComparerQcm,
    comparer_probabilites_tirages_ordonner:   genComparerOrdonner,
    comparer_probabilites_tirages_texte:      genComparerTexte,

    fabriquer_situation_probabilite_texte:        genFabriquerTexte,
    fabriquer_situation_probabilite_classer:      genFabriquerClasser,
    fabriquer_situation_probabilite_qcm_multiple: genFabriquerQcmMulti,
};

// ─── Catégories ───
const FORMAT_LABELS = {
    qcm_unique: 'QCM', qcm_multiple: 'QCM Multi', texte: 'Saisie',
    texte_trou_drag: 'Drag', texte_trou_saisie: 'Saisie trous',
    relier: 'Relier', tableau_vf: 'Vrai/Faux', classer: 'Classer', ordonner: 'Ordonner',
    programme_construction: 'Construction',
};

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'calcul_probabilite_equiprobabilite', label: L_CALCUL,    formatsList: buildFormats('calcul_probabilite_equiprobabilite') },
    { id: 'comparer_probabilites_tirages',      label: L_COMPARER,  formatsList: buildFormats('comparer_probabilites_tirages') },
    { id: 'fabriquer_situation_probabilite',    label: L_FABRIQUER, formatsList: buildFormats('fabriquer_situation_probabilite') },
];

// ─── Point d'entrée ───
export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    // La clé du FORMAT résolu, TOUJOURS (pas celle de la carte posée par les
    // sous-générateurs) : c est elle que la carte d exerciseur compare pour
    // ses compteurs et que la réutilisation range (bug des compteurs muets
    // avant tout clic sur un onglet, tranché 2026-09-02).
    payload._meta.generatorKey = key;
    return payload;
};

export const _internals = {
    simplifier, fractionStr, decimalStr, pourcentageStr,
    genCompositionUrne, genCompositionTrio, descCompositionUrne,
    couleurAccord, articleSg,
    COULEURS, PRENOMS, OBJETS,
};
